'use client';

import React, { useState, useRef, useEffect } from 'react';
import AppLayout from '@/components/AppLayout';
import {
  Search, MapPin, ChevronDown, X, ChevronLeft, ChevronRight,
  Barcode, Layers, RefreshCw
} from 'lucide-react';

// ─── Kit Inventory Types ──────────────────────────────────────────────────────
interface KitInventoryItem {
  id: string;
  kitBarcode: string;
  kitCode: string;
  kitName: string;
  version: string;
  rackNumber: string;
  quantity: number;
  kittedDate: string;
  status: 'Available' | 'Reserved' | 'Dispatched' | 'Quarantine';
}

const MOCK_KIT_INVENTORY: KitInventoryItem[] = [
  { id: '1', kitBarcode: 'KBAR-20260712-001', kitCode: 'KIT-001', kitName: 'Basic First Aid Kit', version: 'v2.5.0', rackNumber: 'KR-A-01', quantity: 24, kittedDate: '12 Jul 2026', status: 'Available' },
  { id: '2', kitBarcode: 'KBAR-20260712-002', kitCode: 'KIT-001', kitName: 'Basic First Aid Kit', version: 'v2.5.0', rackNumber: 'KR-A-01', quantity: 10, kittedDate: '12 Jul 2026', status: 'Reserved' },
  { id: '3', kitBarcode: 'KBAR-20260711-003', kitCode: 'KIT-002', kitName: 'Surgical Procedure Kit', version: 'v2.5.1', rackNumber: 'KR-B-03', quantity: 15, kittedDate: '11 Jul 2026', status: 'Available' },
  { id: '4', kitBarcode: 'KBAR-20260710-004', kitCode: 'KIT-002', kitName: 'Surgical Procedure Kit', version: 'v2.5.1', rackNumber: 'KR-B-03', quantity: 8, kittedDate: '10 Jul 2026', status: 'Dispatched' },
  { id: '5', kitBarcode: 'KBAR-20260709-005', kitCode: 'KIT-003', kitName: 'IV Therapy Kit', version: 'v2.4.1', rackNumber: 'KR-C-02', quantity: 5, kittedDate: '09 Jul 2026', status: 'Quarantine' },
  { id: '6', kitBarcode: 'KBAR-20260708-006', kitCode: 'KIT-001', kitName: 'Basic First Aid Kit', version: 'v2.5.0', rackNumber: 'KR-A-02', quantity: 20, kittedDate: '08 Jul 2026', status: 'Available' },
];

const STATUS_OPTIONS = ['Available', 'Reserved', 'Dispatched', 'Quarantine'];
const PAGE_SIZE_OPTIONS = [10, 50, 100, 200];

const statusColor = (status: string) => {
  if (status === 'Available') return 'text-emerald-600 bg-emerald-50 border border-emerald-200';
  if (status === 'Reserved') return 'text-blue-600 bg-blue-50 border border-blue-200';
  if (status === 'Dispatched') return 'text-gray-500 bg-gray-50 border border-gray-200';
  return 'text-amber-600 bg-amber-50 border border-amber-200';
};

// ─── Searchable Filter ────────────────────────────────────────────────────────
interface SearchableFilterProps {
  label: string;
  value: string;
  options: string[];
  onChange: (val: string) => void;
}

function SearchableFilter({ label, value, options, onChange }: SearchableFilterProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter((o) => o.toLowerCase().includes(query.toLowerCase()));

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-1 focus:ring-primary/50 min-w-[130px] ${value ? 'border-primary/50 bg-primary/5 text-foreground' : 'border-border bg-card text-muted-foreground'}`}
      >
        <span className="flex-1 text-left truncate">{value || label}</span>
        <ChevronDown size={13} className={`flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-48 bg-card border border-border rounded-xl shadow-lg overflow-hidden">
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                autoFocus
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search ${label.toLowerCase()}…`}
                className="w-full pl-7 pr-2 py-1.5 rounded-lg border border-border bg-background text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
              />
            </div>
          </div>
          <div className="max-h-44 overflow-y-auto">
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); setQuery(''); }}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-muted/50 transition-colors ${!value ? 'text-primary font-semibold bg-primary/5' : 'text-muted-foreground'}`}
            >
              All {label}s
            </button>
            {filtered.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => { onChange(opt); setOpen(false); setQuery(''); }}
                className={`w-full text-left px-3 py-2 text-xs hover:bg-muted/50 transition-colors ${value === opt ? 'text-primary font-semibold bg-primary/5' : 'text-foreground'}`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function KitInventoryPage() {
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRack, setFilterRack] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const allRacks = Array.from(new Set(MOCK_KIT_INVENTORY.map((k) => k.rackNumber).filter(Boolean)));

  const filtered = MOCK_KIT_INVENTORY.filter((k) => {
    const matchSearch = !search ||
      k.kitBarcode.toLowerCase().includes(search.toLowerCase()) ||
      k.kitCode.toLowerCase().includes(search.toLowerCase()) ||
      k.kitName.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !filterStatus || k.status === filterStatus;
    const matchRack = !filterRack || k.rackNumber === filterRack;
    return matchSearch && matchStatus && matchRack;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const endIdx = Math.min(startIdx + pageSize, filtered.length);
  const paginated = filtered.slice(startIdx, endIdx);

  const resetPage = () => setCurrentPage(1);

  const getPageNumbers = () => {
    const pages: (number | '...')[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (safePage > 3) pages.push('...');
      for (let i = Math.max(2, safePage - 1); i <= Math.min(totalPages - 1, safePage + 1); i++) pages.push(i);
      if (safePage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const totalQty = filtered.reduce((sum, k) => sum + k.quantity, 0);
  const availableCount = filtered.filter((k) => k.status === 'Available').length;
  const hasFilters = search || filterStatus || filterRack;

  return (
    <AppLayout>
      <div className="flex flex-col h-full min-h-screen bg-background">
        <div className="flex-1 flex flex-col gap-4 p-5 overflow-hidden">

          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                <Layers size={18} className="text-primary" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-foreground">Kit Inventory</h1>
                <p className="text-xs text-muted-foreground">Kitted stock and rack locations</p>
              </div>
            </div>
            <button
              onClick={() => { setSearch(''); setFilterStatus(''); setFilterRack(''); resetPage(); }}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <RefreshCw size={14} /> Refresh
            </button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-card border border-border rounded-xl px-4 py-3">
              <p className="text-xs text-muted-foreground">Total Kits</p>
              <p className="text-xl font-bold text-foreground mt-0.5">{MOCK_KIT_INVENTORY.length}</p>
            </div>
            <div className="bg-card border border-border rounded-xl px-4 py-3">
              <p className="text-xs text-muted-foreground">Available</p>
              <p className="text-xl font-bold text-emerald-600 mt-0.5">{MOCK_KIT_INVENTORY.filter((k) => k.status === 'Available').length}</p>
            </div>
            <div className="bg-card border border-border rounded-xl px-4 py-3">
              <p className="text-xs text-muted-foreground">Reserved</p>
              <p className="text-xl font-bold text-blue-600 mt-0.5">{MOCK_KIT_INVENTORY.filter((k) => k.status === 'Reserved').length}</p>
            </div>
            <div className="bg-card border border-border rounded-xl px-4 py-3">
              <p className="text-xs text-muted-foreground">Total Qty</p>
              <p className="text-xl font-bold text-foreground mt-0.5">{MOCK_KIT_INVENTORY.reduce((s, k) => s + k.quantity, 0)}</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-48">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search barcode, kit code or name…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); resetPage(); }}
                className="w-full pl-8 pr-3 py-2 rounded-lg border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
              />
            </div>
            <SearchableFilter
              label="Status"
              value={filterStatus}
              options={STATUS_OPTIONS}
              onChange={(v) => { setFilterStatus(v); resetPage(); }}
            />
            <SearchableFilter
              label="Rack"
              value={filterRack}
              options={allRacks}
              onChange={(v) => { setFilterRack(v); resetPage(); }}
            />
            {hasFilters && (
              <button
                onClick={() => { setSearch(''); setFilterStatus(''); setFilterRack(''); resetPage(); }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <X size={12} /> Clear
              </button>
            )}
          </div>

          {/* Stats row */}
          <div className="flex items-center gap-4">
            <span className="text-xs text-muted-foreground">{filtered.length} of {MOCK_KIT_INVENTORY.length} records</span>
            <span className="text-xs text-muted-foreground">·</span>
            <span className="text-xs text-emerald-600">{availableCount} available</span>
            <span className="text-xs text-muted-foreground">·</span>
            <span className="text-xs text-muted-foreground">Total qty: {totalQty}</span>
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto rounded-xl border border-border bg-card">
            <table className="w-full text-sm min-w-[800px]">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Kit Barcode</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Kit Code</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Kit Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Version</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Rack No.</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Qty</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Kitted Date</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-16 text-muted-foreground text-sm">No kit inventory records found.</td>
                  </tr>
                ) : paginated.map((item, idx) => (
                  <tr key={item.id} className={`border-b border-border/50 hover:bg-muted/30 transition-colors ${idx % 2 !== 0 ? 'bg-muted/10' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Barcode size={14} className="text-muted-foreground flex-shrink-0" />
                        <span className="font-mono text-xs font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded">{item.kitBarcode}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs font-semibold text-foreground bg-muted px-2 py-0.5 rounded">{item.kitCode}</span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-foreground text-sm">{item.kitName}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-medium text-violet-600 bg-violet-50 border border-violet-200 px-2 py-0.5 rounded-full">{item.version}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <MapPin size={13} className="text-orange-500 flex-shrink-0" />
                        <span className="font-mono text-xs font-semibold text-orange-600 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded">
                          {item.rackNumber || '—'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-semibold text-foreground">{item.quantity}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-muted-foreground">{item.kittedDate}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusColor(item.status)}`}>{item.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Rows per page:</span>
              <div className="flex items-center gap-1">
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <button
                    key={size}
                    onClick={() => { setPageSize(size); resetPage(); }}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${pageSize === size ? 'bg-primary text-white' : 'border border-border text-muted-foreground hover:text-foreground hover:bg-muted'}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {filtered.length === 0 ? '0' : `${startIdx + 1}–${endIdx}`} of {filtered.length}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={14} />
                </button>
                {getPageNumbers().map((pg, i) =>
                  pg === '...' ? (
                    <span key={`ellipsis-${i}`} className="px-1 text-xs text-muted-foreground">…</span>
                  ) : (
                    <button
                      key={pg}
                      onClick={() => setCurrentPage(pg as number)}
                      className={`w-7 h-7 rounded-lg text-xs font-medium transition-colors ${safePage === pg ? 'bg-primary text-white' : 'border border-border text-muted-foreground hover:text-foreground hover:bg-muted'}`}
                    >
                      {pg}
                    </button>
                  )
                )}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </AppLayout>
  );
}
